import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests up to 2 times
      retry: 2,
      // Refetch on window focus (can be disabled per-query)
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// Query key factories for type-safe, consistent keys
export const queryKeys = {
  // Plugins
  plugins: {
    all: ['plugins'] as const,
    lists: () => [...queryKeys.plugins.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.plugins.lists(), filters] as const,
    details: () => [...queryKeys.plugins.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.plugins.details(), id] as const,
    statistics: () => [...queryKeys.plugins.all, 'statistics'] as const,
    recentlyInstalled: () => [...queryKeys.plugins.all, 'recently-installed'] as const,
    myPlugins: () => [...queryKeys.plugins.all, 'my-plugins'] as const,
    configs: () => [...queryKeys.plugins.all, 'config'] as const,
    config: (pluginId: string) => [...queryKeys.plugins.configs(), pluginId] as const,
    // Plugin setup/registration keys
    registrations: () => [...queryKeys.plugins.all, 'registrations'] as const,
    registration: (pluginId: string, workflowId?: string) => 
      [...queryKeys.plugins.registrations(), pluginId, workflowId] as const,
    tenantConnections: (pluginId: string, tenantId: string) => 
      [...queryKeys.plugins.registrations(), 'tenant', pluginId, tenantId] as const,
    // Plugin lifecycle configuration
    lifecycleConfig: (pluginId: string) => 
      [...queryKeys.plugins.configs(), 'lifecycle', pluginId] as const,
  },
  // Admin Plugins (separate cache for admin views)
  adminPlugins: {
    all: ['admin-plugins'] as const,
    lists: () => [...queryKeys.adminPlugins.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.adminPlugins.lists(), filters] as const,
    details: () => [...queryKeys.adminPlugins.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.adminPlugins.details(), id] as const,
    statistics: () => [...queryKeys.adminPlugins.all, 'statistics'] as const,
  },
  // Billing Parameters
  billingParameters: {
    all: ['billing-parameters'] as const,
    lists: () => [...queryKeys.billingParameters.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.billingParameters.lists(), filters] as const,
    details: () => [...queryKeys.billingParameters.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.billingParameters.details(), id] as const,
    detailByCode: (code: string) => [...queryKeys.billingParameters.details(), 'code', code] as const,
    statistics: () => [...queryKeys.billingParameters.all, 'statistics'] as const,
  },
  // Plans (legacy flat model)
  plans: {
    all: ['plans'] as const,
    lists: () => [...queryKeys.plans.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.plans.lists(), filters] as const,
    details: () => [...queryKeys.plans.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.plans.details(), id] as const,
    detailByCode: (code: string) => [...queryKeys.plans.details(), 'code', code] as const,
    statistics: () => [...queryKeys.plans.all, 'statistics'] as const,
  },
  // Billing Plans (normalized model EF-259)
  billingPlans: {
    all: ['billing-plans'] as const,
    lists: () => [...queryKeys.billingPlans.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.billingPlans.lists(), filters] as const,
    details: () => [...queryKeys.billingPlans.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.billingPlans.details(), id] as const,
    statistics: () => [...queryKeys.billingPlans.all, 'statistics'] as const,
  },
  // Credits (Platform Admin)
  adminCredits: {
    all: ['admin-credits'] as const,
    balances: () => [...queryKeys.adminCredits.all, 'balances'] as const,
    balanceList: (filters: Record<string, unknown>) => [...queryKeys.adminCredits.balances(), filters] as const,
    tenantBalance: (tenantId: string) => [...queryKeys.adminCredits.balances(), tenantId] as const,
    statistics: () => [...queryKeys.adminCredits.all, 'statistics'] as const,
    transactions: (tenantId: string, filters?: Record<string, unknown>) => 
      [...queryKeys.adminCredits.all, 'transactions', tenantId, filters] as const,
    grants: (tenantId: string, filters?: Record<string, unknown>) => 
      [...queryKeys.adminCredits.all, 'grants', tenantId, filters] as const,
    grant: (grantId: number) => [...queryKeys.adminCredits.all, 'grant', grantId] as const,
    wallets: (tenantId: string) => [...queryKeys.adminCredits.all, 'wallets', tenantId] as const,
  },
  // Credits (Tenant view)
  credits: {
    all: ['credits'] as const,
    balance: (tenantId?: string) => [...queryKeys.credits.all, 'balance', tenantId] as const,
    transactions: (tenantId?: string, filters?: Record<string, unknown>) => 
      [...queryKeys.credits.all, 'transactions', tenantId, filters] as const,
  },
  // Admin Users
  adminUsers: {
    all: ['admin-users'] as const,
    lists: () => [...queryKeys.adminUsers.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.adminUsers.lists(), filters] as const,
    details: () => [...queryKeys.adminUsers.all, 'detail'] as const,
    detail: (keycloakUserId: string) => [...queryKeys.adminUsers.details(), keycloakUserId] as const,
    statistics: () => [...queryKeys.adminUsers.all, 'statistics'] as const,
  },
  // Admin Tenants
  adminTenants: {
    all: ['admin-tenants'] as const,
    lists: () => [...queryKeys.adminTenants.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.adminTenants.lists(), filters] as const,
    details: () => [...queryKeys.adminTenants.all, 'detail'] as const,
    detail: (tenantId: string) => [...queryKeys.adminTenants.details(), tenantId] as const,
    stats: (tenantId: string) => [...queryKeys.adminTenants.all, 'stats', tenantId] as const,
    members: (tenantId: string) => [...queryKeys.adminTenants.all, 'members', tenantId] as const,
    statistics: () => [...queryKeys.adminTenants.all, 'statistics'] as const,
  },
  // Admin Email Audit Logs
  adminEmailAuditLogs: {
    all: ['admin-email-audit-logs'] as const,
    lists: () => [...queryKeys.adminEmailAuditLogs.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.adminEmailAuditLogs.lists(), filters] as const,
  },
  // Workflows (for future migration)
  workflows: {
    all: ['workflows'] as const,
    lists: () => [...queryKeys.workflows.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.workflows.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.workflows.all, 'detail', id] as const,
  },
  // Credentials
  credentials: {
    all: ['credentials'] as const,
    lists: () => [...queryKeys.credentials.all, 'list'] as const,
    list: (tenantId: string, workspaceId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.credentials.lists(), tenantId, workspaceId, filters] as const,
    details: () => [...queryKeys.credentials.all, 'detail'] as const,
    detail: (tenantId: string, workspaceId: string, credentialId: string) =>
      [...queryKeys.credentials.details(), tenantId, workspaceId, credentialId] as const,
    usage: (tenantId: string, workspaceId: string, credentialId: string) =>
      [...queryKeys.credentials.all, 'usage', tenantId, workspaceId, credentialId] as const,
  },
  // Credential Types
  credentialTypes: {
    all: ['credential-types'] as const,
    lists: () => [...queryKeys.credentialTypes.all, 'list'] as const,
    list: (tenantId: string, workspaceId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.credentialTypes.lists(), tenantId, workspaceId, filters] as const,
    details: () => [...queryKeys.credentialTypes.all, 'detail'] as const,
    detail: (tenantId: string, workspaceId: string, typeKey: string) =>
      [...queryKeys.credentialTypes.details(), tenantId, workspaceId, typeKey] as const,
  },
} as const;
